import { FC, MouseEventHandler, ReactNode, useId } from "react";
import slugify from "slugify";
import classnames from "classnames";

import { BaseAccordion } from "./base-accordion";
import { Skill, SkillGroup } from "../../hooks/use-skills";

export interface SubjectGroupsAccordionHeaderProps {
  isSubGroup?: boolean;
  groupName: string;
}

export const SubjectGroupsAccordionHeader: FC<SubjectGroupsAccordionHeaderProps> = ({
  groupName
}) => {
  return (
    <>
      <div className="accordion-header-container">
        {groupName}
      </div>
    </>
  );
};

const TableWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <table className="table table-hover table-cursor-normal table-collapse-xs table-has-icons">
    <thead>
      <tr>
        <th>Subject</th>
        <th>Virtual</th>
        <th>In Person</th>
      </tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);

export interface SkillsAccordionProps {
  skillGroups: SkillGroup[];
  onSelectSkill: (skill: Skill, field: 'canTeachVirtual' | 'canTeachInPerson', value: boolean) => void;
  readOnly: boolean;
}

export const SkillsAccordion: FC<SkillsAccordionProps> = ({
  skillGroups,
  readOnly,
  onSelectSkill
}) => {
  const getAccordionItems = (
    skillGroups: SkillGroup[]
  ): { header: ReactNode; body: ReactNode }[] => {
    return skillGroups.map(skillGroup => {
      const accordionItems = skillGroup.group?.length
        ? getAccordionItems(skillGroup.group)
        : undefined;

      return {
        header: (
          <SubjectGroupsAccordionHeader
            groupName={skillGroup.displayName}
          />
        ),
        body: (
          <>
            {!!skillGroup.skills?.length && (
              <TableWrapper>
                {skillGroup.skills.map((subject, index) => (
                  <SubjectRow
                    subject={subject}
                    key={index}
                    readOnly={readOnly}
                    onToggleVirtual={() => onSelectSkill(subject, 'canTeachVirtual', !subject.canTeachVirtual)}
                    onToggleInPerson={() => onSelectSkill(subject, 'canTeachInPerson', !subject.canTeachInPerson)}
                  />
                ))}
              </TableWrapper>
            )}
            {!!accordionItems && <BaseAccordion items={accordionItems} darkHeaders={true} />}
          </>
        )
      };
    });
  };
  const accordionItems = getAccordionItems(skillGroups);

  return <BaseAccordion items={accordionItems} />;
};

interface SubjectRowProps {
  subject: Skill;
  readOnly: boolean;
  onToggleVirtual: () => void;
  onToggleInPerson: () => void;
}

const SubjectRow: FC<SubjectRowProps> = ({ subject, readOnly, onToggleVirtual, onToggleInPerson }) => {
  const virtualId = useId();
  const inPersonId = useId();
  return (
    <tr>
      <td className="td-primary">{subject.skillName}</td>

      <td className="td-icon td-highlight">
        <div className={classnames("styled-check", { disabled: readOnly })}>
          <label htmlFor={`toggleVirtual${virtualId}`}>
            <span className="sr-only">{subject.skillName} Virtual</span>
            <input
              className="form-check-input"
              type="checkbox"
              name={`toggleVirtual${virtualId}`}
              id={`toggleVirtual${virtualId}`}
              checked={subject.canTeachVirtual}
              onClick={onToggleVirtual}
              data-testid={"item-virtual-" + slugify(subject.skillName)}
              disabled={readOnly}
              readOnly
            />
            <span className="icon"></span>
          </label>
        </div>
      </td>

      <td className="td-icon td-highlight">
        <div className={classnames("styled-check", { disabled: readOnly })}>
          <label htmlFor={`toggleInPerson${inPersonId}`}>
            <span className="sr-only">{subject.skillName} In Person</span>
            <input
              className="form-check-input"
              type="checkbox"
              name={`toggleInPerson${inPersonId}`}
              id={`toggleInPerson${inPersonId}`}
              checked={subject.canTeachInPerson}
              onClick={onToggleInPerson}
              data-testid={"item-in-person-" + slugify(subject.skillName)}
              disabled={readOnly}
              readOnly
            />
            <span className="icon"></span>
          </label>
        </div>
      </td>
    </tr>
  );
};
